import { ttsApi } from './api';

/**
 * 音声合成サービス
 * GCP Text-to-Speechを使用したテキスト読み上げ機能を提供
 */
export class SpeechSynthesisService {
  private speechQueue: string[] = [];
  private isProcessing: boolean = false;
  private currentAudio: HTMLAudioElement | null = null;
  private onSpeakingStateChange?: (isSpeaking: boolean) => void;

  // 文章分割・並列処理用
  private parallelAudioCache: Map<string, Blob> = new Map();
  private currentSegmentIndex: number = 0;
  private totalSegments: number = 0;
  private isParallelMode: boolean = false;

  /**
   * コンストラクタ
   * @param onSpeakingStateChange 読み上げ状態変更時のコールバック
   */
  constructor(onSpeakingStateChange?: (isSpeaking: boolean) => void) {
    this.onSpeakingStateChange = onSpeakingStateChange;
  }

  /**
   * テキストを音声読み上げキューに追加
   * @param text 読み上げるテキスト
   * @param enableSplitting 長文分割を有効にするか（デフォルト: true）
   */
  speak(text: string, enableSplitting: boolean = true): void {
    console.log('🔊 テキストをキューに追加:', text.substring(0, 50) + '...');
    console.log('📊 現在のキュー状況:', {
      queueLength: this.speechQueue.length,
      isProcessing: this.isProcessing,
      isSpeaking: this.isSpeaking,
      enableSplitting,
    });

    // 長文分割処理
    if (enableSplitting && this.shouldSplitText(text)) {
      console.log('✂️ 長文を検出、分割処理を開始');
      this.speakWithSplitting(text);
      return;
    }

    this.speechQueue.push(text);

    // 現在処理中でなければ処理開始
    if (!this.isProcessing) {
      console.log('🚀 キュー処理を開始します');
      this.processQueue();
    } else {
      console.log('⏳ 既に処理中のため、キューに追加のみ');
    }
  }

  /**
   * テキストを分割すべきかどうかを判定
   * @param text テキスト
   * @returns 分割すべきかどうか
   */
  private shouldSplitText(text: string): boolean {
    // 50文字以上、かつ句読点が含まれている場合は分割対象
    const hasDelimiters = /[。！？]/.test(text);
    const isLongText = text.length > 50;

    return isLongText && hasDelimiters;
  }

  /**
   * テキストを分割して並列処理で音声生成
   * @param text 分割対象のテキスト
   */
  private async speakWithSplitting(text: string): Promise<void> {
    try {
      // 現在の音声を中断しない（キューに追加するだけ）
      // this.stopCurrentAudio(); ← この行をコメントアウト

      // テキストを分割
      const segments = this.splitText(text);
      console.log('📝 テキスト分割結果:', segments);

      if (segments.length <= 1) {
        // 分割できない場合は通常処理（キューに追加）
        this.speechQueue.push(text);
        if (!this.isProcessing && !this.isParallelMode) {
          this.processQueue();
        }
        return;
      }

      // 分割TTSをキューに追加して順番を待つ
      console.log('🗓️ 分割TTSをキューに追加:', segments.length + '個のセグメント');

      // 特別なマーカーで分割TTSとしてキューに追加
      this.speechQueue.push(`__SPLIT_TTS_START__:${JSON.stringify(segments)}`);

      // キュー処理が停止している場合は開始
      if (!this.isProcessing && !this.isParallelMode) {
        console.log('🚀 キュー処理を開始（分割TTS対応）');
        this.processQueue();
      }
    } catch (error) {
      console.error('❌ 分割TTS処理エラー:', error);
      // フォールバック: 通常処理でキューに追加
      this.speechQueue.push(text);
      if (!this.isProcessing && !this.isParallelMode) {
        this.processQueue();
      }
    }
  }

  /**
   * 分割TTSを実際に実行（キューから呼び出される）
   * @param segments 分割されたテキストセグメント
   */
  private async executeSplitTTS(segments: string[]): Promise<void> {
    try {
      // 並列モード開始
      this.isParallelMode = true;
      this.currentSegmentIndex = 0;
      this.totalSegments = segments.length;
      this.parallelAudioCache.clear();

      console.log('🚀 分割TTS実行開始:', segments.length + '個のセグメント');

      // 全セグメントを並列でTTS処理
      const ttsPromises = segments.map((segment, index) =>
        this.generateSegmentAudio(segment, index)
      );

      // 最初のセグメントの完了を待って再生開始
      const firstAudio = await ttsPromises[0];
      if (firstAudio && this.isParallelMode) {
        this.notifySpeakingState(true);
        await this.playSegmentsSequentially(segments, ttsPromises);
      }
    } catch (error) {
      console.error('❌ 分割TTS実行エラー:', error);
      this.isParallelMode = false;
      this.notifySpeakingState(false);
    }
  }

  /**
   * テキストを句読点で分割
   * @param text 分割対象のテキスト
   * @returns 分割されたテキストの配列
   */
  private splitText(text: string): string[] {
    // 句読点で分割
    const segments = text.split(/([。！？])/).filter((segment) => segment.trim().length > 0);

    // 句読点を前の文に結合
    const result: string[] = [];
    for (let i = 0; i < segments.length; i += 2) {
      const content = segments[i];
      const delimiter = segments[i + 1] || '';
      const combined = (content + delimiter).trim();

      if (combined.length > 0) {
        result.push(combined);
      }
    }

    // 短すぎるセグメントを前のセグメントと結合
    const minLength = 10;
    const finalResult: string[] = [];

    for (const segment of result) {
      if (segment.length < minLength && finalResult.length > 0) {
        // 前のセグメントと結合
        finalResult[finalResult.length - 1] += segment;
      } else {
        finalResult.push(segment);
      }
    }

    return finalResult;
  }

  /**
   * セグメントの音声を生成
   * @param segment テキストセグメント
   * @param index セグメントのインデックス
   * @returns 音声Blob
   */
  private async generateSegmentAudio(segment: string, index: number): Promise<Blob | null> {
    try {
      console.log(
        `🎵 セグメント${index + 1}/${this.totalSegments}の音声生成開始:`,
        segment.substring(0, 30) + '...'
      );

      const startTime = performance.now();
      const audioBlob = await ttsApi.synthesizeSpeech(segment);
      const endTime = performance.now();

      console.log(
        `✅ セグメント${index + 1}の音声生成完了 (${(endTime - startTime).toFixed(1)}ms):`,
        segment.substring(0, 30) + '...'
      );

      // キャッシュに保存
      this.parallelAudioCache.set(`segment_${index}`, audioBlob);

      return audioBlob;
    } catch (error) {
      console.error(`❌ セグメント${index + 1}の音声生成エラー:`, error);
      return null;
    }
  }

  /**
   * セグメントを順次再生
   * @param segments テキストセグメント配列
   * @param ttsPromises TTS生成Promise配列
   */
  private async playSegmentsSequentially(
    segments: string[],
    ttsPromises: Promise<Blob | null>[]
  ): Promise<void> {
    for (let i = 0; i < segments.length; i++) {
      try {
        // 並列モードがキャンセルされた場合は中断
        if (!this.isParallelMode) {
          console.log('🚫 並列モードがキャンセルされたため、再生中断');
          break;
        }

        console.log(`🎶 セグメント${i + 1}/${segments.length}の再生開始`);

        // 該当セグメントの音声生成完了を待機
        const audioBlob = await ttsPromises[i];

        if (!audioBlob) {
          console.warn(`⚠️ セグメント${i + 1}の音声生成に失敗、スキップ`);
          continue;
        }

        // 音声再生（キャンセルチェック付き）
        if (this.isParallelMode) {
          await this.playAudioBlob(audioBlob);
          console.log(`✅ セグメント${i + 1}の再生完了`);
        }

        // 短い間隔で次のセグメントへ
        if (i < segments.length - 1 && this.isParallelMode) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`❌ セグメント${i + 1}の再生エラー:`, error);
      }
    }

    // 並列モード終了
    this.isParallelMode = false;
    this.isProcessing = false; // キュー処理を再開
    this.parallelAudioCache.clear();
    this.notifySpeakingState(false);

    console.log('🎉 分割TTS再生完了');

    // キューに残っているメッセージがあれば処理を続行
    if (this.speechQueue.length > 0) {
      console.log('🔄 分割TTS完了後、キュー処理を再開');
      setTimeout(() => this.processQueue(), 100);
    }
  }

  /**
   * 音声Blobを再生
   * @param audioBlob 音声Blob
   */
  private playAudioBlob(audioBlob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      this.currentAudio = audio;

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        resolve();
      };

      audio.onerror = (error) => {
        console.error('❌ セグメント音声再生エラー:', error);
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        reject(error);
      };

      audio.play().catch(reject);
    });
  }

  /**
   * 音声読み上げキューを処理
   */
  private async processQueue(): Promise<void> {
    console.log('🔄 processQueue 開始 - キュー長:', this.speechQueue.length);

    // 並列モード中はキュー処理をスキップ
    if (this.isParallelMode) {
      console.log('⏳ 並列モード中のためキュー処理をスキップ');
      return;
    }

    if (this.speechQueue.length === 0) {
      console.log('✅ キューが空のため処理終了');
      this.isProcessing = false;
      this.notifySpeakingState(false);
      return;
    }

    this.isProcessing = true;
    const text = this.speechQueue.shift();
    console.log('🎯 次のテキストを処理:', text?.substring(0, 50) + '...');

    if (text) {
      // 分割TTSマーカーをチェック
      if (text.startsWith('__SPLIT_TTS_START__:')) {
        try {
          const segmentsJson = text.replace('__SPLIT_TTS_START__:', '');
          const segments = JSON.parse(segmentsJson) as string[];
          console.log('🔄 分割TTS処理開始:', segments.length + '個のセグメント');

          await this.executeSplitTTS(segments);

          // 分割TTS完了後、次のキューアイテムを処理
          setTimeout(() => {
            this.processQueue();
          }, 50);

          return;
        } catch (error) {
          console.error('❌ 分割TTSマーカーの解析エラー:', error);
          // エラー時は次のアイテムを処理
          setTimeout(() => {
            this.processQueue();
          }, 50);
          return;
        }
      }

      // 通常のTTS処理
      try {
        console.log('🎵 音声合成開始:', text.substring(0, 50) + '...');

        // GCP TTSで音声を生成
        const audioBlob = await ttsApi.synthesizeSpeech(text);

        // 並列モードが開始された場合はキューに戻して待機
        if (this.isParallelMode) {
          console.log('⏳ 並列モード開始のため、テキストをキューに戻して待機');
          this.speechQueue.unshift(text); // キューの先頭に戻す
          this.isProcessing = false;
          return;
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        this.currentAudio = audio;

        // 音声再生の準備
        const playAudio = (): Promise<void> => {
          return new Promise((resolve, reject) => {
            audio.onended = () => {
              console.log('🎵 音声再生完了');
              URL.revokeObjectURL(audioUrl);
              this.currentAudio = null;

              // 現在の音声が終了したことを通知
              this.notifySpeakingState(false);

              // 次のキューアイテムを処理（パフォーマンス最適化で遅延短縮）
              setTimeout(() => {
                this.processQueue();
              }, 50); // 遅延を300msから50msに短縮

              resolve();
            };

            audio.onerror = (error) => {
              console.error('🔴 音声再生エラー:', error);
              URL.revokeObjectURL(audioUrl);
              this.currentAudio = null;

              // エラー時も話し終わったことを通知
              this.notifySpeakingState(false);

              // エラー時も次のキューアイテムを処理（パフォーマンス最適化）
              setTimeout(() => {
                this.processQueue();
              }, 50);

              reject(error);
            };

            // 音声再生開始
            audio.play().catch((playError) => {
              console.error('🔴 音声再生開始エラー:', playError);
              URL.revokeObjectURL(audioUrl);
              this.currentAudio = null;
              this.notifySpeakingState(false);

              // エラー時も次のキューアイテムを処理（パフォーマンス最適化）
              setTimeout(() => {
                this.processQueue();
              }, 50);

              reject(playError);
            });
          });
        };

        // 読み上げ状態を通知
        this.notifySpeakingState(true);
        await playAudio();
      } catch (error) {
        console.error('🔴 TTS処理エラー:', error);
        console.error('🔴 処理に失敗したテキスト:', text);

        // 話し終わったことを通知
        this.notifySpeakingState(false);

        // エラー時も次のキューアイテムを処理（パフォーマンス最適化）
        setTimeout(() => {
          console.log('🔄 エラー後、次のキューアイテムを処理します');
          this.processQueue();
        }, 100); // 遅延を500msから100msに短縮
      }
    }
  }

  /**
   * 現在の音声再生を停止
   */
  stop(): void {
    console.log('⏹️ 音声再生を停止');

    this.stopCurrentAudio();

    // キューをクリア
    this.speechQueue = [];
    this.isProcessing = false;
    this.isParallelMode = false;
    this.parallelAudioCache.clear();
    this.notifySpeakingState(false);
  }

  /**
   * 現在の音声を停止（内部メソッド）
   */
  private stopCurrentAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  }

  /**
   * キューをクリア（再生中の音声は継続）
   */
  clearQueue(): void {
    console.log('🗑️ 音声キューをクリア');
    this.speechQueue = [];
  }

  /**
   * 読み上げ中かどうかを取得
   */
  get isSpeaking(): boolean {
    return (this.isProcessing && this.currentAudio !== null) || this.isParallelMode;
  }

  /**
   * キューの残り件数を取得
   */
  get queueLength(): number {
    return this.speechQueue.length;
  }

  /**
   * 読み上げ状態の変更を通知
   * @param isSpeaking 読み上げ中かどうか
   */
  private notifySpeakingState(isSpeaking: boolean): void {
    if (this.onSpeakingStateChange) {
      this.onSpeakingStateChange(isSpeaking);
    }
  }

  /**
   * サービスを破棄（クリーンアップ）
   */
  dispose(): void {
    this.stop();
    this.onSpeakingStateChange = undefined;
  }
}

/**
 * デフォルトの音声合成サービスインスタンス
 */
export const speechSynthesisService = new SpeechSynthesisService();
